import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Autocomplete,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    FormControl,
    Button, Snackbar, Alert
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import {
    DatePicker,
    LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, formatISO, set } from 'date-fns';
import { axiosInstance,localAxiosGet, localAxiosToken } from '../../../Axios/axios';
import axios from 'axios';

const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3b82f6',
        },
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3b82f6',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#3b82f6',
    },
};

const EditProjectDetails = ({ selectedProjectId, managerOptions, teamLeadOptions, onClose }) => {
    const [projectDetails, setProjectDetails] = useState({});
    const [editedDetails, setEditedDetails] = useState({
        project_title: '',
        priority: '',
        status: '',
        startDate: null,
        endDate: null,
        projectManager: null,
        teamLead: null,
        description: '',
        progresspercentage: '',
        client: ''
    });
    const [initialDetails, setInitialDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const accessToken = localStorage.getItem('accessToken');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

     // Check if there are any changes
    const hasChanges = () => {
        // Compare current editedDetails with initialDetails
        return JSON.stringify(editedDetails) !== JSON.stringify(initialDetails);
    };

    useEffect(() => {

        const fetchProjectDetails = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance(localAxiosGet(`/project/getProjectById/${selectedProjectId}`, accessToken))
                if (response.data.status === 'SUCCESS') {
                    setProjectDetails(response.data.message.data);

                    const initialStatus = response.data.message.data?.project?.status || '';
                    const initialProgress = response.data.message.data?.project?.progress?.toString() || '0';
                    const newEditedDetails = ({
                        project_title: response.data.message.data?.project?.project_title || '',
                        priority: response.data.message.data?.project?.priority || '',
                        status: initialStatus,
                        startDate: response.data.message.data?.project?.start_date ? parseISO(response.data.message.data.project.start_date) : null,
                        endDate: response.data.message.data?.project?.end_date ? parseISO(response.data.message.data.project.end_date) : null,
                        projectManager: managerOptions?.find(option => option.id === response.data.message.data?.manager?.id) || null,
                        teamLead: teamLeadOptions?.find(option => option.id === response.data.message.data?.team_leader?.id) || null,
                        description: response.data.message.data?.project?.description || '',
                        progresspercentage: initialStatus === 'Completed' ? '100' : initialProgress,
                        client: response.data.message.data?.clients?.[0]?.first_name && response.data.message.data?.clients?.[0]?.last_name
                            ? `${response.data.message.data.clients[0].first_name} ${response.data.message.data.clients[0].last_name}`
                            : '',
                    });
                     setEditedDetails(newEditedDetails);
                    setInitialDetails(newEditedDetails); 
                }
            } catch (error) {
                console.log('Error in Fetching the Project Details', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProjectDetails();
    }, [selectedProjectId, managerOptions, teamLeadOptions]);

    const handleSaveButton = async () => {

        if (!hasChanges()) {
            setSnackbar({ 
                open: true, 
                message: 'No changes to save', 
                severity: 'info' 
            });
            return;
        }
        const payload = { project_id: selectedProjectId };
        const initialProject = projectDetails?.project;
        const initialManager = projectDetails?.manager;
        const initialTeamLead = projectDetails?.team_leader;
        const initialClient = projectDetails?.clients?.[0]?.first_name + ' ' + projectDetails?.clients?.[0]?.last_name || '';
        const initialManagerName = initialManager?.first_name + ' ' + initialManager?.last_name || '';
        const initialTeamLeadName = initialTeamLead?.first_name + ' ' + initialTeamLead?.last_name || '';
        const editedManagerName = editedDetails.projectManager?.first_name + ' ' + editedDetails.projectManager?.last_name || '';
        const editedTeamLeadName = editedDetails.teamLead?.first_name + ' ' + editedDetails.teamLead?.last_name || '';

        if (editedDetails.project_title !== initialProject?.project_title) {
            payload.project_title = editedDetails.project_title;
        }
        if (editedDetails.priority !== initialProject?.priority) {
            payload.priority = editedDetails.priority;
        }
        if (editedDetails.status !== initialProject?.status) {
            payload.status = editedDetails.status;
        }
        if (editedDetails.startDate && initialProject?.start_date && formatISO(editedDetails.startDate) !== formatISO(parseISO(initialProject.start_date))) {
            payload.start_date = formatISO(editedDetails.startDate);
        } else if (editedDetails.startDate && !initialProject?.start_date) {
            payload.start_date = formatISO(editedDetails.startDate);
        } else if (!editedDetails.startDate && initialProject?.start_date) {
            payload.start_date = null;
        }

        if (editedDetails.endDate && initialProject?.end_date && formatISO(editedDetails.endDate) !== formatISO(parseISO(initialProject.end_date))) {
            payload.end_date = formatISO(editedDetails.endDate);
        } else if (editedDetails.endDate && !initialProject?.end_date) {
            payload.end_date = formatISO(editedDetails.endDate);
        } else if (!editedDetails.endDate && initialProject?.end_date) {
            payload.end_date = null;
        }

        if (editedManagerName !== initialManagerName) {
            payload.manager_id = editedDetails.projectManager?.id || null;
        }

        if (editedTeamLeadName !== initialTeamLeadName) {
            payload.team_leader_id = editedDetails.teamLead?.id || null;
        }

        if (editedDetails.description !== initialProject?.description) {
            payload.description = editedDetails.description;
        }
        if (parseInt(editedDetails.progresspercentage, 10) !== initialProject?.progress) {
            payload.progress = parseInt(editedDetails.progresspercentage, 10);
        }
        if (editedDetails.client !== initialClient) {
            const [firstName, ...rest] = editedDetails.client.split(' ');
            const lastName = rest.join(' ');
            payload.clients = [{ first_name: firstName, last_name: lastName }];
        }

        if (Object.keys(payload).length > 1) {
            try {
                const response = await axiosInstance(localAxiosToken('/project/updateProjectDetails', payload));
                if (response.data.status === 'SUCCESS') {
                    if (response.data && response.data.status === 'SUCCESS') {
                        // Show success snackbar first
                        setSnackbar({
                            open: true,
                            message: response.data.message.message || 'Project details updated successfully!',
                            severity: 'success'
                        });
                         setInitialDetails({ ...editedDetails });
                        // Then close the window and reset form after a short delay
                        setTimeout(() => {
                            onClose();
                            // Reset form
                            setEditedDetails({
                                project_title: '',
                                priority: '',
                                status: '',
                                startDate: null,
                                endDate: null,
                                projectManager: null,
                                teamLead: null,
                                description: '',
                                progresspercentage: '',
                                client: ''
                            });
                            setLoading(false);
                        }, 500);

                    } else if (response.data.status === 'FAILED') {
                        // Handle the case where message is an object with error property
                        const errorMessage = typeof response.data.message === 'object'
                            ? response.data.message.error
                            : response.data.message;
                        setSnackbar({ open: true, message: errorMessage || 'Failed to update project details.', severity: 'error' });
                        setLoading(false);
                    }
                }
            } catch (error) {
                console.error('Error in creating the project', error.response ? error.response.data : error.message);
                // Handle nested error objects properly
                const errorData = error?.response?.data;
                let errorMessage = 'Error while updating project. Please try again.';

                if (typeof errorData?.message === 'object' && errorData.message.error) {
                    errorMessage = errorData.message.error;
                } else if (errorData?.message) {
                    errorMessage = errorData.message;
                } else if (errorData?.error) {
                    errorMessage = errorData.error;
                }

                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                setLoading(false); // Stop loading on error
            }
        }
    }
    const handleCloseEditDetails = () => {
        setEditedDetails({
            project_title: '',
            priority: '',
            status: '',
            startDate: null,
            endDate: null,
            projectManager: null,
            teamLead: null,
            description: '',
            progresspercentage: '',
            client: ''
        });
         setInitialDetails({});
        onClose();
    }

    const handleStatusChange = (event) => {
        const newStatus = event.target.value;
        setEditedDetails({
            ...editedDetails,
            status: newStatus,
            progresspercentage: newStatus === 'Completed' ? '100' : editedDetails.progresspercentage,
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <LinearProgress sx={{ width: '50%' }} />
            </Box>
        );
    }

    return (
        <Box
    sx={{
      maxHeight: '650px',       
      overflowY: 'auto',         
      paddingRight: 1,           
    }}
  >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                p: 1,
                borderRadius: '4px',
                color: 'white',
            }}>
                <Edit sx={{ mr: 1, color: 'white', fontSize: 22 }} />
                <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                    Edit Project Details
                </Typography>
            </Box>

            <Grid container spacing={1.5} marginTop={1}>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Project Name"
                        fullWidth
                        required
                        value={editedDetails.project_title}
                        onChange={(e) => setEditedDetails({ ...editedDetails, project_title: e.target.value })}
                        sx={textFieldStyles}
                    />
                </Grid>


                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Client"
                        fullWidth
                        required
                        value={editedDetails.client}
                        onChange={(e) => setEditedDetails({ ...editedDetails, client: e.target.value })}
                        sx={textFieldStyles}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        options={managerOptions}
                        getOptionLabel={(option) => option.name}
                        value={editedDetails.projectManager}
                        onChange={(event, newValue) => {
                            setEditedDetails({ ...editedDetails, projectManager: newValue });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Project Manager"
                                fullWidth
                                required
                            />
                        )}
                    />
                </Grid>


                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        options={teamLeadOptions}
                        getOptionLabel={(option) => option.name}
                        value={editedDetails.teamLead}
                        onChange={(event, newValue) => {
                            setEditedDetails({ ...editedDetails, teamLead: newValue });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Team Lead"
                                fullWidth
                                required
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Start Date"
                            value={editedDetails.startDate}
                            format="dd/MM/yyyy"
                            onChange={(date) => setEditedDetails({ ...editedDetails, startDate: date })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    required
                                    sx={textFieldStyles}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="End Date"
                            value={editedDetails.endDate}
                            format="dd/MM/yyyy"
                            onChange={(date) => setEditedDetails({ ...editedDetails, endDate: date })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    required
                                    sx={textFieldStyles}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Grid>


                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel sx={{ '&.Mui-focused': { color: '#3b82f6' } }}>Priority</InputLabel>
                        <Select
                            value={editedDetails.priority}
                            label="Priority"
                            onChange={(e) => setEditedDetails({ ...editedDetails, priority: e.target.value })}
                            sx={textFieldStyles}
                        >
                            <MenuItem value="Low" sx={{ color: '#4caf50' }}>Low</MenuItem>
                            <MenuItem value="Medium" sx={{ color: '#ff9800' }}>Medium</MenuItem>
                            <MenuItem value="High" sx={{ color: '#f44336' }}>High</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>


                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel sx={{ '&.Mui-focused': { color: '#3b82f6' } }}>Status</InputLabel>
                        <Select
                            value={editedDetails.status}
                            label="Status"
                            onChange={handleStatusChange}
                            sx={textFieldStyles}
                        >
                            <MenuItem value="Todo">Todo</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Overdue">Overdue</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={editedDetails.description}
                        onChange={(e) => setEditedDetails({ ...editedDetails, description: e.target.value })}
                        sx={textFieldStyles}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{
                        p: 2,
                        borderRadius: '12px',
                        background: 'linear-gradient(to right, #f8f9ff, #ffffff)',
                        border: '1px solid #e0e7ff'
                    }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#3b82f6', fontWeight: 600 }}>
                            Project Progress
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={editedDetails.progresspercentage}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: '#e0e7ff',
                                '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(to right, #3b82f6, #2563eb)'
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>0%</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>100%</Typography>
                        </Box>
                        <TextField
                            type="number"
                            label="Progress Percentage"
                            value={editedDetails.progresspercentage}
                            onChange={(e) => setEditedDetails({
                                ...editedDetails,
                                progresspercentage: e.target.value === '' ? '' : Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                            })}
                            InputProps={textFieldStyles}
                            fullWidth
                        />
                    </Box>
                </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#1976d2',
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                        },
                    }}
                    disabled={!hasChanges()}
                    onClick={handleSaveButton}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                        borderRadius: '8px',
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        '&:hover': {
                            backgroundColor: '#e0f2f7',
                            borderColor: '#1e88e5',
                            color: '#1e88e5',
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                        },
                    }}
                    onClick={handleCloseEditDetails}
                >
                    Close
                </Button>

            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EditProjectDetails;

